import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Typography, Space, Spin, message, Modal, Form } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import Layouts from "../../../components/Layouts";
import {
  eliminarCampoFormulario,
  obtenerFormulario,
  actualizarCampoFormulario,
  actualizarOrdenCampo,
  crearCampoFormulario,
  obtenerSiguienteOrden,
} from "../../../services/formularios";
import FormularioHeader from "./components/FormularioHeader";
import CampoFormulario from "./components/CampoFormulario";
import ModalCampo from "./components/ModalCampo";

const { Title } = Typography;
const { confirm } = Modal;

const VerFormulario = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formulario, setFormulario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isNewField, setIsNewField] = useState(false);
  const [form] = Form.useForm();
  const [selectedTipo, setSelectedTipo] = useState(null);

  useEffect(() => {
    cargarFormulario();
  }, [id]);

  const cargarFormulario = async () => {
    try {
      setLoading(true);
      const data = await obtenerFormulario(id);
      data.campos_formulario.sort((a, b) => (a.orden || 0) - (b.orden || 0));
      setFormulario(data);
    } catch (error) {
      message.error("Error al cargar el formulario");
      console.error(error);
      navigate("/formularios_reporte");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCampo = (campoId) => {
    confirm({
      title: "¿Estás seguro de que quieres eliminar este campo?",
      icon: <ExclamationCircleOutlined />,
      content: "Esta acción no se puede deshacer.",
      okText: "Sí, eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await eliminarCampoFormulario(campoId);
          message.success("Campo eliminado exitosamente");
          cargarFormulario();
        } catch (error) {
          message.error("Error al eliminar el campo");
        }
      },
    });
  };

  const generateUniqueKey = (label) => {
    const randomNum = Math.floor(Math.random() * 10000);
    return `${label.toLowerCase().replace(/\s+/g, "")}${randomNum}`;
  };

  const handleNewField = () => {
    setIsNewField(true);
    setEditingField(null);
    form.resetFields();
    setSelectedTipo(null);
    setIsModalVisible(true);
  };

  const handleEdit = (campo) => {
    setIsNewField(false);
    setEditingField(campo);
    let opcionesFormateadas = "";

    if (campo.opciones) {
      try {
        if (typeof campo.opciones === "string") {
          const cleanString = campo.opciones.replace(/[{}"]/g, "");
          const pairs = cleanString.split(",").map((pair) => pair.trim());
          const values = pairs
            .filter((pair) => pair.includes(":"))
            .map((pair) => {
              const [_, value] = pair.split(":").map((s) => s.trim());
              return value;
            });
          opcionesFormateadas = values.join(", ");
        } else {
          opcionesFormateadas = Object.values(campo.opciones).join(", ");
        }
      } catch (error) {
        console.error("Error al formatear opciones:", error);
        opcionesFormateadas = "";
      }
    }

    form.setFieldsValue({
      label: campo.label,
      tipo: campo.tipo,
      requerido: campo.requerido,
      visible_app: campo.visible_app,
      opciones: opcionesFormateadas,
      camara: campo.camara || false,
      galeria: campo.galeria || false,
    });
    setSelectedTipo(campo.tipo);
    setIsModalVisible(true);
  };

  const handleSave = async (values) => {
    try {
      const campoData = {
        label: values.label,
        tipo: values.tipo,
        requerido: values.requerido,
        visible_app: values.visible_app,
      };

      if (isNewField) {
        campoData.clave = generateUniqueKey(values.label);
        const siguienteOrden = await obtenerSiguienteOrden(id);
        campoData.orden = siguienteOrden;
      }

      if (values.tipo === "imagen") {
        campoData.camara = values.camara || false;
        campoData.galeria = values.galeria || false;
      }

      if (
        ["select", "multiple_unico", "multiple_multiple"].includes(
          values.tipo
        ) &&
        values.opciones
      ) {
        const opcionesArray = values.opciones.split(",").map(opt => opt.trim());
        campoData.opciones = opcionesArray.reduce((acc, opt) => {
          acc[opt] = opt;
          return acc;
        }, {});
      }

      if (isNewField) {
        await crearCampoFormulario(id, campoData);
        message.success("Campo creado exitosamente");
      } else {
        await actualizarCampoFormulario(editingField.id, campoData);
        message.success("Campo actualizado exitosamente");
      }

      setIsModalVisible(false);
      setEditingField(null);
      setIsNewField(false);
      cargarFormulario();
    } catch (error) {
      message.error("Error al guardar el campo");
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingField(null);
    setIsNewField(false);
    form.resetFields();
  };

  const handleOrdenChange = async (campoId, nuevoOrden) => {
    try {
      const campoConNuevoOrden = formulario.campos_formulario.find(
        (campo) => campo.orden === nuevoOrden && campo.id !== campoId
      );

      const campoActual = formulario.campos_formulario.find(
        (campo) => campo.id === campoId
      );

      if (campoConNuevoOrden) {
        await actualizarOrdenCampo(campoConNuevoOrden.id, campoActual.orden);
      }

      await actualizarOrdenCampo(campoId, nuevoOrden);
      message.success("Orden actualizado exitosamente");
      cargarFormulario();
    } catch (error) {
      message.error("Error al actualizar el orden");
    }
  };

  const generarOpcionesOrden = (campoActual) => {
    const totalCampos = formulario.campos_formulario.length;
    const opciones = [];

    for (let i = 1; i <= totalCampos; i++) {
      opciones.push({
        value: i,
        label: `Orden ${i}`,
      });
    }

    return opciones;
  };

  if (loading) {
    return (
      <Layouts>
        <div style={{ padding: "24px", textAlign: "center" }}>
          <Spin size="large" />
        </div>
      </Layouts>
    );
  }

  if (!formulario) {
    return null;
  }

  return (
    <Layouts>
      <div style={{ padding: "24px" }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Card>
            <Space direction="vertical" style={{ width: "100%" }}>
              <FormularioHeader
                formulario={formulario}
                onNewField={handleNewField}
              />
              <Title level={4}>Campos del Formulario</Title>
              <Space direction="vertical" style={{ width: "100%" }}>
                {formulario?.campos_formulario?.map((campo) => (
                  <CampoFormulario
                    key={campo.id}
                    campo={campo}
                    onEdit={handleEdit}
                    onDelete={handleDeleteCampo}
                    onOrdenChange={handleOrdenChange}
                    opcionesOrden={generarOpcionesOrden(campo)}
                  />
                ))}
              </Space>
            </Space>
          </Card>
        </Space>

        <ModalCampo
          isVisible={isModalVisible}
          isNewField={isNewField}
          onCancel={handleCancel}
          onSave={handleSave}
          form={form}
          selectedTipo={selectedTipo}
          setSelectedTipo={setSelectedTipo}
          existingFields={formulario?.campos_formulario || []}
          editingField={editingField}
        />
      </div>
    </Layouts>
  );
};

export default VerFormulario;
